import { collection, query, where, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions, COLLECTIONS } from "../config/firebase";

// Server-authoritative check-in (server-checkin-redesign.md). Firestore
// rules can only constrain the SHAPE of a write, never "did the client
// actually validate a scanned QR token / rate limit / admin role before
// writing this" — so the mint/check-in/manual-mark logic that used to run
// as direct client Firestore reads+writes here now runs behind three Cloud
// Functions (firebase-functions/eventCheckIn.js), which re-derive every
// check this file used to do client-side with the Admin SDK, unforgeable.
const mintEventQRTokenCallable = httpsCallable(functions, "mintEventQRToken");
const checkInWithQRTokenCallable = httpsCallable(functions, "checkInWithQRToken");
const markAttendanceManuallyCallable = httpsCallable(functions, "markAttendanceManually");
const unmarkAttendanceManuallyCallable = httpsCallable(functions, "unmarkAttendanceManually");
// deleteOwnEventAttendance's client-facing wrapper (round-2 gate correction,
// Finding 1) was REMOVED in server-checkin-redesign.md §15.2 — account
// deletion no longer calls a standalone attendance-cleanup callable at all.
// AuthService.deleteAccountWithoutPassword now calls a single "deleteMyAccount"
// callable (firebase-functions/index.js) that drains event_attendees,
// cascades the rest of a departing user's data, and deletes the Firebase
// Auth account atomically server-side — nothing in this file needs a
// separate wrapper for that any more.

export class AttendanceService {
  // ---------------------------------------------------------------------
  // QR check-in (SP-2) — the ONLY attendee self-check-in path. The manual
  // 6-digit OTP path (generateEventOTP/checkInWithOTP and friends) was
  // removed 2026-08-19 per founder decision. Every event has QR check-in
  // (TF227, 2026-10-15): checkInWithQRToken ignores the legacy
  // `qr_checkin_enabled` field; admins can still mark attendance manually
  // via manuallyMarkAttendance below.
  //
  // The QR payload is a fresh per-event random token displayed by the admin.
  // Students scan it and the service-layer call validates the token against
  // what's stored on the event doc. Threat model: anyone in the room can
  // self-check-in by scanning the admin's screen. Token rotates per event;
  // embedding eventId in the payload prevents cross-event screenshot replay.
  //
  // Every method below is now a THIN WRAPPER around one of the three
  // callables above — no direct getDoc/runTransaction here anymore. Each
  // preserves its pre-existing function signature and (via
  // _mapCallableError) its exact {data, error} shape and error-message
  // copy, so AdminScreen.js / QRScannerScreen.js / EventDetailsScreen.js /
  // EventQRDisplayScreen.js needed NO call-site changes.
  // ---------------------------------------------------------------------

  // vlchk:v2: is what mintEventQRToken now mints; vlchk:v1: is the legacy
  // client-minted prefix a not-yet-updated admin build may still display
  // during the migration window (server-checkin-redesign.md §6) — the scan
  // side must keep accepting both.
  static QR_PAYLOAD_PREFIXES = ["vlchk:v1:", "vlchk:v2:"];

  // Firebase's callable protocol passes an HttpsError's `message` straight
  // through as `error.message` on the client, so most failures need no
  // translation at all here — the Cloud Function's HttpsError message text
  // IS the copy this app already shows (see eventCheckIn.js). The one
  // exception is checkin_window_closed: EventQRDisplayScreen.js branches on
  // error.code for it, but HttpsError's fixed code enum has no room for a
  // custom string, so the Function carries it in `details.reason` instead
  // (the one field httpsCallable errors preserve verbatim) and this
  // reconstructs the sentinel shape the screen already expects.
  static _mapCallableError(error) {
    if (error?.details?.reason === "checkin_window_closed") {
      return {
        code: "checkin_window_closed",
        message: "Check-in window has closed for this event.",
      };
    }
    return { code: error?.code, message: error?.message || "Something went wrong. Please try again." };
  }

  /**
   * Generate (or rotate) the QR token displayed by the admin for an event.
   * Returns the full scannable payload `vlchk:v2:{eventId}:{token}`. Server
   * reuses the stored token if still within the 10-minute freshness window.
   */
  static async generateEventQRToken(eventId) {
    try {
      if (!eventId) {
        return { data: null, error: { message: "eventId required" } };
      }

      const result = await mintEventQRTokenCallable({ eventId });
      return {
        data: {
          payload: result.data?.payload,
          token: result.data?.token,
          generated_at: result.data?.generated_at,
        },
        error: null,
      };
    } catch (error) {
      if (__DEV__) console.error("Generate event QR token error:", error);
      return { data: null, error: AttendanceService._mapCallableError(error) };
    }
  }

  /**
   * Verify a scanned QR payload and check the user in: rate limit, check-in
   * window, dedupe, increment counters — all now enforced server-side by
   * checkInWithQRToken against the token stored on the event doc.
   */
  static async checkInWithQRCode(scannedPayload) {
    try {
      if (!scannedPayload || typeof scannedPayload !== "string") {
        return { data: null, error: { message: "Invalid QR code" } };
      }
      // Fast client-side pre-filter for an obviously-non-VibeLink scan (any
      // other app's QR code) so a mis-scan doesn't cost a round trip. This
      // is a UX shortcut ONLY — the callable re-validates the exact same
      // shape server-side regardless, since a scripted client could call it
      // directly without ever going through this check.
      const hasKnownPrefix = AttendanceService.QR_PAYLOAD_PREFIXES.some((p) =>
        scannedPayload.startsWith(p)
      );
      if (!hasKnownPrefix) {
        return {
          data: null,
          error: { message: "QR code is not a VibeLink check-in code" },
        };
      }

      const result = await checkInWithQRTokenCallable({ scannedPayload });
      return { data: result.data, error: null };
    } catch (error) {
      if (__DEV__) console.error("Check in with QR error:", error);
      return { data: null, error: AttendanceService._mapCallableError(error) };
    }
  }

  /**
   * Manually mark attendance (admin only, for past events). `adminId` is
   * accepted for call-site/signature compatibility only — the callable
   * derives "who is marking this" solely from its own authenticated
   * request.auth.uid, never from a client-supplied value.
   */
  static async manuallyMarkAttendance(eventId, userId, _adminId) {
    try {
      const result = await markAttendanceManuallyCallable({ eventId, userId });
      return { data: result.data, error: null };
    } catch (error) {
      if (__DEV__) console.error("Manually mark attendance error:", error);
      return { data: null, error: AttendanceService._mapCallableError(error) };
    }
  }

  /**
   * Undo a MANUAL attendance mark (an accidental tap in Mark attendance).
   * The callable only reverses check_in_method "manual" rows — a student's
   * own QR check-in is never undone here.
   */
  static async unmarkAttendance(eventId, userId) {
    try {
      const result = await unmarkAttendanceManuallyCallable({ eventId, userId });
      return { data: result.data, error: null };
    } catch (error) {
      if (__DEV__) console.error("Unmark attendance error:", error);
      return { data: null, error: AttendanceService._mapCallableError(error) };
    }
  }

  /**
   * Get attendance status for a user and event. A plain read, not part of
   * the write path this redesign closes — but (event_id, user_id) is not
   * unique by design (the RSVP flow can legitimately create a second,
   * never-checked-in row for the same pair — see
   * server-checkin-redesign.md §12.3/§11.6, which fixed the equivalent bug
   * server-side in eventCheckIn.js's checkInWithQRToken/
   * markAttendanceManually). Firestore's default order for an equality-only
   * query is by document id, so if a lower-sorting "shadow" row happens to
   * be the caller's plain RSVP row while a higher-sorting row is their real
   * checked-in one, taking only docs[0] can report checked_in:false for a
   * user who actually IS checked in. This is a read-only display function
   * (not a security boundary — nothing is authorized off this result), but
   * showing "not checked in" to a checked-in user is a real, user-visible
   * bug (e.g. EventDetailsScreen's "unchecked attendees" list would
   * wrongly include them). Search every doc this query returns for one that
   * is actually checked in before falling back to docs[0], matching the
   * server-side fix's approach.
   */
  static async getUserAttendanceStatus(eventId, userId) {
    try {
      const attendanceQuery = query(
        collection(db, COLLECTIONS.EVENT_ATTENDEES),
        where("event_id", "==", eventId),
        where("user_id", "==", userId)
      );
      const attendanceSnapshot = await getDocs(attendanceQuery);

      if (attendanceSnapshot.empty) {
        return { data: { checked_in: false }, error: null };
      }

      const checkedInDoc = attendanceSnapshot.docs.find(
        (doc) => doc.data().checked_in === true
      );
      const attendanceData = (checkedInDoc || attendanceSnapshot.docs[0]).data();
      return { data: attendanceData, error: null };
    } catch (error) {
      if (__DEV__) console.error("Get user attendance status error:", error);
      return { data: null, error };
    }
  }
}
